import { SecureStorage } from "@aparajita/capacitor-secure-storage";
import { Capacitor } from "@capacitor/core";
import { z } from "zod";

// The native shell's origin (capacitor://localhost, https://localhost) is never
// same-site with the API, so the WebView drops the session cookies the web
// app relies on (#225). Native carries its session itself instead:
// - the access token lives only in memory and goes out as X-Session-Token
//   (on dev, Authorization is already taken by IAP's own token);
// - the refresh token lives in the iOS Keychain / Android Keystore — never
//   localStorage — and goes in the body of /auth/refresh and /auth/logout.
// Losing the in-memory access token on relaunch is fine: the first request
// 401s and the client's normal refresh path restores it from the refresh token.

const REFRESH_TOKEN_KEY = "refresh_token";
// Never sync the token to iCloud Keychain: a session belongs to one device.
const SYNC = false;

/** What the backend returns to native clients from sign-in and refresh (AuthSuccess). */
export const NativeSessionSchema = z.object({
  access_token: z.string().min(1),
  refresh_token: z.string().min(1),
});
export type NativeSession = z.infer<typeof NativeSessionSchema>;

let accessToken: string | null = null;

/** Whether this is the Capacitor native app (as opposed to the web app). */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/** The current access token, if one was issued since launch. */
export function getAccessToken(): string | null {
  return accessToken;
}

/** The stored refresh token, or null when signed out. */
export async function getRefreshToken(): Promise<string | null> {
  const value = await SecureStorage.get(REFRESH_TOKEN_KEY, false, SYNC);
  return typeof value === "string" ? value : null;
}

/** Keep a newly issued session: access token in memory, refresh token in secure storage. */
export async function saveSession(session: NativeSession): Promise<void> {
  accessToken = session.access_token;
  await SecureStorage.set(REFRESH_TOKEN_KEY, session.refresh_token, false, SYNC);
}

/** Forget the session locally (no-op on web). Callers revoke it server-side first when they can. */
export async function clearSession(): Promise<void> {
  if (!isNativeApp()) return;
  accessToken = null;
  await SecureStorage.remove(REFRESH_TOKEN_KEY, SYNC);
}

/** Body for /auth/refresh and /auth/logout: the stored refresh token on native; none on web (cookie). */
export async function refreshTokenBody(): Promise<{ refresh_token: string | null } | undefined> {
  return isNativeApp() ? { refresh_token: await getRefreshToken() } : undefined;
}
